import React, { useId, useState } from "react";
import { fieldShellClass, useFormValidation } from "./FormValidationContext";

interface TextInputProps {
  label: string;
  name: string;
  value: string;
  para?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  optional?: boolean;
  widthLabel?: string;
  widthInput?: string;
  stacked?: boolean;
  /** Override validation error state */
  error?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  label,
  name,
  value,
  para,
  onChange,
  optional = false,
  stacked = false,
  error,
}) => {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const { showErrors } = useFormValidation();
  const invalid =
    error ?? (showErrors && !optional && !String(value ?? "").trim());

  const labelEl = (
    <label
      htmlFor={id}
      className={`text-xs font-medium ${
        invalid ? "text-[var(--cu-danger)]" : "text-[var(--cu-muted)]"
      } ${stacked ? "mb-1.5 block" : "shrink-0 pt-2 md:w-[32%]"}`}
    >
      {label}
      {!optional && <span className="ml-1 text-[var(--cu-danger)]">*</span>}
      {optional && (
        <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-subtle)]">
          optional
        </span>
      )}
      {invalid && (
        <span className="ml-1.5 text-[11px] font-normal text-[var(--cu-danger)]">
          Required
        </span>
      )}
      {para && (
        <span className="mt-0.5 block text-[11px] font-normal leading-snug text-[var(--cu-subtle)]">
          {para}
        </span>
      )}
    </label>
  );

  const inputEl = (
    <input
      id={id}
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      aria-invalid={invalid}
      className={`w-full rounded-lg border bg-[var(--cu-input)] px-3 py-2 text-sm text-[var(--cu-text)] outline-none transition-colors placeholder:text-[var(--cu-placeholder)] ${
        stacked ? "" : "flex-1"
      } ${
        invalid
          ? "border-[var(--cu-danger)]"
          : focused
            ? "border-[var(--cu-accent)]"
            : "border-[var(--cu-border)] hover:border-[var(--cu-border-hover)]"
      }`}
    />
  );

  return (
    <div className={fieldShellClass(invalid)} data-invalid={invalid || undefined}>
      {stacked ? (
        <div>
          {labelEl}
          {inputEl}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:gap-4">
          {labelEl}
          {inputEl}
        </div>
      )}
    </div>
  );
};

export default TextInput;
